import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { CalendarDays, TrendingUp, AlertCircle, Loader2 } from "lucide-react";

const LEAVE_TYPE_COLORS = {
  vacation:    "bg-blue-100 text-blue-800",
  sick:        "bg-red-100 text-red-800",
  maternity:   "bg-pink-100 text-pink-800",
  paternity:   "bg-purple-100 text-purple-800",
  unpaid:      "bg-gray-100 text-gray-700",
  bereavement: "bg-slate-100 text-slate-700",
  other:       "bg-yellow-100 text-yellow-800",
};

export default function MesSoldes() {
  const { t } = useTranslation();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const typeLabel = (type) => t(`portal.balances.types.${type}`, { defaultValue: type });

  useEffect(() => {
    apiClient.get("v1/leave-policies/balances/me")
      .then(r => setBalances(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading")}
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 py-8 text-red-500">
      <AlertCircle className="h-5 w-5" /> {error}
    </div>
  );

  if (balances.length === 0) return (
    <div className="text-center py-16 text-muted-foreground">
      <CalendarDays className="h-10 w-10 mx-auto mb-2 text-gray-300" />
      <p>{t("portal.balances.empty")}</p>
      <p className="text-xs mt-1">{t("portal.balances.emptyHint")}</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("portal.balances.intro")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {balances.map((b) => {
          const pct = b.max_balance ? Math.min(100, (b.balance / b.max_balance) * 100) : null;
          const isLow = b.balance < 3 && b.leave_type === "vacation";
          const isNegative = b.balance < 0;

          return (
            <div key={b.policy_id} className={`rounded-xl border p-5 ${isNegative ? "border-red-200 bg-red-50/30" : "bg-card"}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${LEAVE_TYPE_COLORS[b.leave_type]}`}>
                    {typeLabel(b.leave_type)}
                  </span>
                  <h3 className="font-semibold text-sm mt-1.5">{b.policy_name}</h3>
                </div>
                <div className={`text-2xl font-bold tabular-nums ${isNegative ? "text-red-500" : isLow ? "text-orange-500" : "text-blue-600"}`}>
                  {b.balance.toFixed(1)}<span className="text-sm font-normal text-muted-foreground ml-0.5">{t("portal.balances.dayShort")}</span>
                </div>
              </div>

              {pct !== null && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{t("portal.balances.currentBalance")}</span>
                    <span>{b.balance.toFixed(1)} / {b.max_balance}{t("portal.balances.dayShort")}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isNegative ? "bg-red-400" : pct > 60 ? "bg-blue-500" : pct > 30 ? "bg-orange-400" : "bg-red-400"}`}
                      style={{ width: `${Math.max(0, pct)}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                {b.accrual_amount > 0 && (
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-green-500" />
                    +{b.accrual_amount}{t("portal.balances.dayShort")} {t("portal.balances.perPeriod")}
                  </div>
                )}
                {b.carryover_days > 0 && (
                  <div>{t("portal.balances.carryoverMax")}: {b.carryover_days}{t("portal.balances.dayShort")}</div>
                )}
                {b.negative_balance_allowed && (
                  <div className="text-orange-500">{t("portal.balances.negativeAllowed")}</div>
                )}
              </div>

              {isLow && !isNegative && (
                <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" /> {t("portal.balances.lowBalance")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
